import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, caseAPI } from '../services/api';
import {
  DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES,
  getCaseStatusNotificationLabel,
} from '../constants/caseStatusNotifications';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Button, 
  CircularProgress, 
  Alert, 
  Breadcrumbs, 
  Link, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
  TextField,
  Autocomplete,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Home as HomeIcon, Folder as ProjectIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ProjectActivitiesManager from '../components/ProjectActivitiesManager';
import { canViewClientBilling } from '../utils/firmAccess';

// Helper function to format case status to human-readable label
const getStatusLabel = (status: string): string => {
  if (!status) return 'N/A';
  
  switch (status.toLowerCase()) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'on_hold':
      return 'On Hold';
    case 'cancelled':
      return 'Cancelled';
    case 'awaiting_info':
      return 'Awaiting Info';
    case 'closed':
      return 'Closed';
    case 'estimate_approved':
      return 'Estimate Approved';
    case 'estimate_declined':
      return 'Estimate Declined';
    case 'estimate_sent':
      return 'Estimate Sent';
    case 'mrr_completed':
      return 'MRR Completed';
    case 'mrr_delivered':
      return 'MRR Delivered';
    case 'na':
      return 'N/A';
    case 'pending':
      return 'Pending';
    case 'quality_check':
      return 'Quality Check';
    case 'quality_error':
      return 'Quality Error';
    case 'reworked':
      return 'Reworked';
    case 'training':
      return 'Training';
    case 'voicemail':
      return 'Voicemail';
    case 'wip':
      return 'WIP';
    case 'delivered':
      return 'Delivered';
    default:
      // Convert snake_case to Title Case
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
};

const statusOptions = [
  'open', 'in_progress', 'completed', 'closed', 'on_hold', 'cancelled',
  'awaiting_info', 'estimate_approved', 'estimate_declined', 'estimate_sent',
  'mrr_completed', 'mrr_delivered', 'na', 'pending', 'quality_check',
  'quality_error', 'reworked', 'training', 'voicemail', 'wip', 'delivered'
];

const ProjectView: React.FC = () => {
  const { projectId, firmId } = useParams<{ projectId: string; firmId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [recentCases, setRecentCases] = useState<any[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  
  // Filter state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [fileNumber, setFileNumber] = useState('');
  const [clientReferenceId, setClientReferenceId] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const canEdit = isPmpAdmin() || isSiteAdmin();

  // Load project data
  useEffect(() => {
    const load = async () => {
      try {
        const res = await projectAPI.getById(Number(projectId));
        const proj = res.data.data;
        setProject(proj);
        // If route not firm-scoped, redirect to canonical firms/:firmId/projects/:projectId when firm id is available
        if (!firmId && proj.firm_id) {
          navigate(`/firms/${proj.firm_id}/projects/${proj.id}`, { replace: true });
          return;
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, firmId, navigate]);

  // Load cases with filters
  useEffect(() => {
    if (!project) return;
    
    const loadCases = async () => {
      setCasesLoading(true);
      try {
        // Build filter parameters
        const filterParams: any = {
          project_id: project.id,
          page,
          per_page: perPage,
        };
        
        // Add filters if they have values
        if (title) filterParams.title = title;
        if (subtitle) filterParams.subtitle = subtitle;
        if (fileNumber) filterParams.file_number = fileNumber;
        if (clientReferenceId) filterParams.client_reference_id = clientReferenceId;
        if (selectedStatuses.length > 0) filterParams.statuses = selectedStatuses;
        
        const casesRes = await caseAPI.getAll(filterParams);
        
        // Handle paginated response - ensure we always get an array
        // Response structure: { success: true, data: { data: [...], total: 5, last_page: 1 } }
        let casesList: any[] = [];
        let totalCount = 0;
        let lastPageNum = 1;
        
        if (casesRes.data) {
          // Check for nested structure: casesRes.data.data.data (array)
          if (casesRes.data.data && casesRes.data.data.data && Array.isArray(casesRes.data.data.data)) {
            casesList = casesRes.data.data.data;
            totalCount = casesRes.data.data.total || 0;
            lastPageNum = casesRes.data.data.last_page || 1;
          } 
          // Check for direct data structure: casesRes.data.data (array)
          else if (casesRes.data.data && Array.isArray(casesRes.data.data)) {
            casesList = casesRes.data.data;
            totalCount = casesRes.data.total || casesList.length;
            lastPageNum = casesRes.data.last_page || 1;
          } 
          // Fallback: casesRes.data is array
          else if (Array.isArray(casesRes.data)) {
            casesList = casesRes.data;
            totalCount = casesList.length;
            lastPageNum = 1;
          }
        }
        
        setRecentCases(casesList);
        setTotal(totalCount);
        setLastPage(lastPageNum);
      } catch (e: any) {
        console.error('Failed to load cases:', e);
        setRecentCases([]);
        setTotal(0);
      } finally {
        setCasesLoading(false);
      }
    };
    
    loadCases();
  }, [project, page, perPage, title, subtitle, fileNumber, clientReferenceId, selectedStatuses]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [title, subtitle, fileNumber, clientReferenceId, selectedStatuses]);
  
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handlePerPageChange = (event: any) => {
    setPerPage(parseInt(event.target.value));
    setPage(1);
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!project) return <Alert severity="warning">Project not found</Alert>;

  const custom = project.settings?.custom_fields || {};
  const showBillingDetails = canViewClientBilling(user);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <HomeIcon sx={{ mr: 1 }} />
          Firms
        </Link>
        {project.firm_id && (
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${project.firm_id}`); }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <BusinessIcon sx={{ mr: 1 }} />
            {project.firm?.name || `Firm #${project.firm_id}`}
          </Link>
        )}
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ProjectIcon sx={{ mr: 1 }} />
          {project.name}
        </Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>{project.name}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        {canEdit && (
          <Button variant="contained" onClick={() => navigate(`/firms/${project.firm_id}/projects/${project.id}/edit`)}>Edit Project</Button>
        )}
        <Button variant="outlined" onClick={() => navigate(`/firms/${project.firm_id}/cases/new`)}>New Case</Button>
        <Button variant="outlined" onClick={() => navigate(`/firms/${project.firm_id}/cases/bulk?project_id=${project.id}`)}>Create cases</Button>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>Description</Typography>
        <Divider sx={{ my: 1 }} />
        <div dangerouslySetInnerHTML={{ __html: project.description }} />

        <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Settings</Typography>
        <Divider sx={{ my: 1 }} />
        {showBillingDetails && (
          <>
            <Typography variant="body2">Billing Type: {custom.billing_type || '-'}</Typography>
            <Typography variant="body2">Rate: {custom.rate ?? '-'}</Typography>
            {custom.billing_type === 'Page Based' && Number(custom.rush_rate) > 0 && (
              <Typography variant="body2">RUSH Rate: {custom.rush_rate}</Typography>
            )}
            {custom.billing_type === 'Page Based' && custom.appendix_c_rate != null && custom.appendix_c_rate !== '' && (
              <Typography variant="body2">Appendix C Rate: {custom.appendix_c_rate}</Typography>
            )}
            {custom.billing_type === 'Page Based' && Array.isArray(custom.appendix_c_rate_ranges) && custom.appendix_c_rate_ranges.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Appendix Rate Ranges:</Typography>
                {custom.appendix_c_rate_ranges.map((range: any, idx: number) => (
                  <Typography key={idx} variant="body2">
                    Pages {range.from}–{range.to ?? '∞'} @ ${range.rate}
                  </Typography>
                ))}
              </Box>
            )}
            {custom.billing_type === 'Consolidation' && custom.appendix_c_rate != null && (
              <Typography variant="body2">Appendix Rate: {custom.appendix_c_rate}</Typography>
            )}
          </>
        )}
        <Typography variant="body2">Free cases: {custom.free_cases_allowed ?? '-'}</Typography>
        <Typography variant="body2">Hide Case Timer: {custom.hide_case_timer ? 'Yes' : 'No'}</Typography>
        <Typography variant="body2">
          Auto-clone to vendor: {project.settings?.auto_assign_vendors ? 'Yes' : 'No'}
        </Typography>
        {project.settings?.auto_assign_vendors && (
          <Typography variant="body2">
            Auto-clone vendor firm ID: {project.settings?.auto_clone_vendor_firm_id || '-'}
            {project.settings?.auto_clone_vendor_user_id ? ` / user #${project.settings.auto_clone_vendor_user_id}` : ''}
          </Typography>
        )}
        <Typography variant="body2">
          Case status change notifications:{' '}
          {(
            project.settings?.notification_settings?.case_status_change_notification_statuses
            ?? DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES
          )
            .map((status: string) => getCaseStatusNotificationLabel(status))
            .join(', ') || 'None'}
        </Typography>
        <Typography variant="body2">
          Also notify Email Settings admins:{' '}
          {project.settings?.notification_settings?.notify_admins_on_case_status_change ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2">
          Due date required on cases:{' '}
          {project.settings?.notification_settings?.allow_optional_due_date !== true ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2">
          Due date set from priority:{' '}
          {project.settings?.notification_settings?.priority_based_due_date === true ? 'Yes' : 'No'}
        </Typography>

        {custom.enable_checklist && (
          <>
            <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Checklist</Typography>
            <Divider sx={{ my: 1 }} />
            <div dangerouslySetInnerHTML={{ __html: custom.checklist_instructions || '' }} />
          </>
        )}

        {custom.enable_disposition && (
          <>
            <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Disposition</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">Mandatory: {custom.mandatory_disposition_field ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2">Options:</Typography>
            <ul>
              {(custom.disposition_list || []).map((d: string) => (<li key={d}>{d}</li>))}
            </ul>
          </>
        )}
      </Paper>

      {project.settings && (project.settings.estimate_type === 'hours' || project.settings.estimate_type === 'both') && (
        <ProjectActivitiesManager
          projectId={project.id}
          isTimeBased={true}
          canManage={canEdit}
        />
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>Cases</Typography>
        <Divider sx={{ my: 1, mb: 2 }} />
        
        {/* Filters */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={2}>
            <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
              <TextField 
                label="Title" 
                fullWidth 
                size="small" 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
              <TextField 
                label="Sub Title" 
                fullWidth 
                size="small" 
                value={subtitle} 
                onChange={(e) => setSubtitle(e.target.value)} 
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', sm: '1 1 260px' }} minWidth={{ xs: '100%', sm: 240 }}>
              <Autocomplete
                multiple
                size="small"
                options={statusOptions}
                getOptionLabel={(o) => getStatusLabel(o)}
                value={selectedStatuses}
                onChange={(_e, vals: string[]) => setSelectedStatuses(vals)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      variant="outlined" 
                      label={getStatusLabel(option)} 
                      {...getTagProps({ index })} 
                      key={option} 
                      size="small" 
                    />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Status" placeholder="Choose some options" />}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
              <TextField 
                label="Client ID" 
                fullWidth 
                size="small" 
                value={clientReferenceId} 
                onChange={(e) => setClientReferenceId(e.target.value)} 
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
              <TextField 
                label="File Number" 
                fullWidth 
                size="small" 
                value={fileNumber} 
                onChange={(e) => setFileNumber(e.target.value)} 
              />
            </Box>
          </Box>
        </Paper>

        {/* Cases Table */}
        {casesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Sub Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Client ID</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>File Number</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(recentCases) ? recentCases : []).map((c) => {
                    const caseFirmId = c.firm_id || project.firm_id;
                    const caseLink = caseFirmId ? `/firms/${caseFirmId}/cases/${c.id}` : null;
                    
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          {caseLink ? (
                            <Link
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(caseLink);
                              }}
                              sx={{ 
                                color: 'primary.main', 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              #{c.id}
                            </Link>
                          ) : (
                            `#${c.id}`
                          )}
                        </TableCell>
                        <TableCell>
                          {caseLink ? (
                            <Link
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(caseLink);
                              }}
                              sx={{ 
                                color: 'primary.main', 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {c.title || c.name}
                            </Link>
                          ) : (
                            c.title || c.name
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {c.subtitle || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={getStatusLabel(c.status)} />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {c.client_reference_id || '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {c.file_number || '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {new Date(c.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!Array.isArray(recentCases) || recentCases.length === 0) && !casesLoading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No cases match your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {!casesLoading && total > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Per Page</InputLabel>
                    <Select
                      value={perPage}
                      label="Per Page"
                      onChange={handlePerPageChange}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} cases
                  </Typography>
                </Box>
                <Pagination
                  count={lastPage}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectView;
